import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './HealingCourseProgress.module.css'
import houseIcon from '../../assets/icons/healingHouseColor.svg'
import treeIcon from '../../assets/icons/healingTreeColor.svg'
import humanIcon from '../../assets/icons/healingHumanColor.svg'
import RainyIcon from '../../assets/icons/healingRainyColor.svg'
import grayHouseIcon from '../../assets/icons/healingHouseGray.svg'
import grayTreeIcon from '../../assets/icons/healingTreeGray.svg'
import grayHumanIcon from '../../assets/icons/healingHumanGray.svg'
import grayRainyIcon from '../../assets/icons/healingRainyGray.svg'
import axios from 'axios'
import { getAccessToken } from '../Auth/AuthStore.ts'
import { AlertModal } from '../AlertModal'


const HealingCourseProgress = ({ progressData = null, onEnrollSuccess, isParticipated = false }) => {
  const navigate = useNavigate()
  const completedCourseCount = Math.max(0, progressData?.completedCourseCount ?? 0)
  const nextDailyCourseId = progressData?.toParticipateDailyCourseId ?? null
  const participated = progressData?.isParticipated ?? isParticipated
  const [showEnrollModal, setShowEnrollModal] = useState(false)
  const [enrollLoading, setEnrollLoading] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')

  const courses = [
    {
      id: 1,
      type: 'htp',
      title: '1일차',
      subtitle: '집',
      color: '#BFDBFE',
      borderColor: '#3B82F6',
      iconColor: houseIcon,
      iconGray: grayHouseIcon,
      dailyCourseId: null,
      topicId: 'htp-house',
    },
    {
      id: 3,
      type: 'dap',
      title: '3일차',
      subtitle: '사람 - 남자',
      color: '#FFF1B8',
      borderColor: '#EAB308',
      iconColor: humanIcon,
      iconGray: grayHumanIcon,
      dailyCourseId: null,
      topicId: 'htp-human',
    },
    {
      id: 5,
      type: 'pitr',
      title: '5일차',
      subtitle: '빗속의 사람',
      color: '#DAFFDA',
      borderColor: '#51B951',
      iconColor: RainyIcon,
      iconGray: grayRainyIcon,
      dailyCourseId: null,
      topicId: 'pitr',
    },
    {
      id: 2,
      type: 'drawing',
      title: '2일차',
      subtitle: '나무',
      color: '#DDD6FE',
      borderColor: '#A855F7',
      iconColor: treeIcon,
      iconGray: grayTreeIcon,
      dailyCourseId: null,
      topicId: 'htp-tree',
    },
    {
      id: 4,
      type: 'family',
      title: '4일차',
      subtitle: '사람 - 여자',
      color: '#FFDCB9',
      borderColor: '#FB923C',
      iconColor: humanIcon,
      iconGray: grayHumanIcon,
      dailyCourseId: null,
      topicId: 'htp-human',
    }
  ]

  const orderedCourses = [...courses].sort((a, b) => a.id - b.id)
  const totalCourses = orderedCourses.length
  const normalizedCompletedCount = Math.min(completedCourseCount, totalCourses)

  const derivedCourses = orderedCourses.map((course) => ({
    ...course,
    completed: course.id <= normalizedCompletedCount
  }))

  const firstIncompleteCourse = derivedCourses.find(course => !course.completed)

  // 모든 코스를 완료했는지 여부 (5일차까지 완료)
  const isCourseFullyCompleted = normalizedCompletedCount >= totalCourses

  const handleProgramCompleteAndNavigate = async () => {
    try {
      const therapyProgramId = progressData?.therapyProgramId
      if (!therapyProgramId) {
        setAlertMessage('진행 중인 치유 프로그램 ID를 찾을 수 없습니다.')
        setShowAlert(true)
        return
      }

      const token = getAccessToken()
      if (!token) {
        setAlertMessage('로그인 정보가 없습니다.')
        setShowAlert(true)
        return
      }

      await axios.patch(
        `${import.meta.env.VITE_API_BASE}/api/programs/${therapyProgramId}/complete`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      setAlertMessage('치유 프로그램 완주 처리가 완료되었습니다!')
      setShowAlert(true)
      setTimeout(() => {
        navigate(`/programs/${therapyProgramId}/result`)
      }, 1000)
    } catch (error) {
      console.error('프로그램 완주 처리 실패:', error)
      setAlertMessage('프로그램 완주 처리 중 오류가 발생했습니다.')
      setShowAlert(true)
    }
  }

  const handleEnroll = async () => {
    try {
      setEnrollLoading(true)
      const token = getAccessToken()
      if (!token) throw new Error('토큰이 없습니다.')

      await axios.post(`${import.meta.env.VITE_API_BASE}/api/programs/enroll`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      setAlertMessage('치유 프로그램 등록이 완료되었습니다!')
      setShowAlert(true)
      setShowEnrollModal(false)
      onEnrollSuccess?.()
    } catch (error) {
      if (error.response?.status === 400) {
        setAlertMessage(error.response?.data?.message || '이미 진행 중인 프로그램이 있습니다.')
      } else {
        setAlertMessage('프로그램 등록 중 오류가 발생했습니다.')
      }
      setShowAlert(true)
      console.error(error)
    } finally {
      setEnrollLoading(false)
    }
  }

  // 모든 코스 완료 시: 코스 카드 대신 "완주하기" 버튼만 보여줌
  if (isCourseFullyCompleted) {
    return (
      <div className={styles.courseContainer}>
        <div className={styles.completeCard}>
          <h4 className={styles.completeTitle}>5일 치유 코스를 모두 완료했어요</h4>
          <p className={styles.completeDescription}>
            지금까지의 여정을 바탕으로 치유 프로그램을 마무리해 볼까요?
          </p>
          <button
            type="button"
            className={styles.completeButton}
            onClick={handleProgramCompleteAndNavigate}
          >
            프로그램 완주하기
          </button>
        </div>
      </div>
    )
  }

  return ( 
    <>
      <div className={styles.courseContainer}>
        {derivedCourses.map((course) => {
        // 실제 코스 순서(id 기준)로 이전 코스들이 모두 완료되었는지 확인
        const previousCourses = derivedCourses.filter(c => c.id < course.id)
        const previousCoursesCompleted = previousCourses.length === 0 || previousCourses.every(c => c.completed)
        const isNextCourse = !course.completed && (
          (nextDailyCourseId && course.dailyCourseId
            ? course.dailyCourseId === nextDailyCourseId
            : course.id === normalizedCompletedCount + 1)
        )

        const isActive = course.completed || isNextCourse
        const isFirstIncomplete = firstIncompleteCourse && course.id === firstIncompleteCourse.id
        const canEnroll = isFirstIncomplete && !participated
        const canStartCourse = isFirstIncomplete && participated

        const cardClasses = [
          styles.courseCard,
          course.completed ? styles.completed : '',
          isActive ? styles.activeCard : '',
          !isActive && !isFirstIncomplete ? styles.inactiveCard : '',
          isFirstIncomplete ? styles.enrollableCard : '',
        ].join(' ')
        
        const backgroundColor = isActive ? course.color : '#E5E7EB'
        const borderColor = isActive ? course.borderColor : '#9CA3AF'
        const icon = isActive ? course.iconColor : course.iconGray

          return (
            <React.Fragment key={course.id}>
              <div 
                className={cardClasses}
                style={{ 
                  background: backgroundColor,
                  borderColor: borderColor
                }}
                onClick={() => {
                  if (canEnroll) {
                    setShowEnrollModal(true)
                  } else if (canStartCourse && course.topicId) {
                    navigate('/drawing', {
                      state: {
                        preselectedTopicId: course.topicId,
                        healingCourse: {
                          courseId: nextDailyCourseId,
                          title: course.title,
                        },
                      },
                    })
                  }
                }}
              >
                <div className={styles.cardIcon}>
                  <img src={icon} alt={course.subtitle} />
                </div>
                <div className={styles.cardTitle}>{course.title}</div>
                <div className={styles.cardSubtitle}>{course.subtitle}</div>
                {course.completed && (
                  <div className={styles.checkmark}>
                    <i className="fas fa-check-circle"></i>
                  </div>
                )}
              </div>
            
            </React.Fragment>
          )
        })}
      </div>

      {showEnrollModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h4>힐링 코스 참여</h4>
            <p className={styles.modalDescription}>
              총 5일 동안 진행되는 치유 프로그램입니다.<br />
              첫 번째 코스부터 차근차근 함께 해볼까요?
            </p>
            <div className={styles.modalButtons}>
              <button 
                type="button" 
                className={styles.modalCancel}
                onClick={() => setShowEnrollModal(false)}
                disabled={enrollLoading}
              >
                취소
              </button>
              <button 
                type="button" 
                className={styles.modalConfirm}
                onClick={handleEnroll}
                disabled={enrollLoading}
              >
                {enrollLoading ? '참여 중...' : '참여하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        message={alertMessage}
      />
    </>
  )
}

export default HealingCourseProgress
